import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Phone, User as UserIcon, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { apiError } from '../lib/api';
import { COUNTRIES } from '../data/constants';

const IconInput = ({ icon: Icon, testId, ...props }) => (
  <div className="relative">
    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A7468]" />
    <Input {...props} data-testid={testId} className="pl-10 h-11" />
  </div>
);

const SubmitButton = ({ children, testId, loading }) => (
  <Button type="submit" disabled={loading} className="w-full h-11 bg-[#0F8B5C] hover:bg-[#0A6E47] text-white font-semibold" data-testid={testId}>
    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{children}
  </Button>
);

const SignIn = () => {
  const { t, country: defaultCountry } = useApp();
  const { login, register } = useAuth();
  const { toast } = useToast();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || '/dashboard';
  const [tab, setTab] = useState(params.get('mode') === 'signup' ? 'signup' : 'signin');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [signin, setSignin] = useState({ email: '', password: '' });
  const [signup, setSignup] = useState({ name: '', email: '', phone: '', password: '', country: defaultCountry });

  const run = async (fn, successKey) => {
    setLoading(true);
    setError('');
    try {
      await fn();
      toast({ title: t(successKey) });
      nav(next);
    } catch (e) {
      setError(apiError(e, t('auth.failed')));
    } finally {
      setLoading(false);
    }
  };
  const onSignin = (e) => { e.preventDefault(); run(() => login(signin.email, signin.password), 'auth.welcomeToast'); };
  const onSignup = (e) => { e.preventDefault(); run(() => register({ ...signup, phone: signup.phone || null }), 'auth.createdToast'); };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12" data-testid="signin-page">
      <div className="w-full max-w-md bg-white rounded-3xl border border-[#EFEAE0] p-8 shadow-sm">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-[#0F8B5C] mx-auto flex items-center justify-center text-white font-black text-2xl mb-4">K</div>
          <h1 className="text-2xl font-black tracking-tight text-[#1A1A1A]">{t('auth.welcome')}</h1>
          <p className="text-sm text-[#7A7468] mt-1">{t('auth.tagline')}</p>
        </div>
        <Tabs value={tab} onValueChange={(v) => { setTab(v); setError(''); }}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="signin" data-testid="tab-signin">{t('auth.signin')}</TabsTrigger>
            <TabsTrigger value="signup" data-testid="tab-signup">{t('auth.signup')}</TabsTrigger>
          </TabsList>
          <TabsContent value="signin">
            <form onSubmit={onSignin} className="space-y-3" data-testid="signin-form">
              <IconInput icon={Mail} type="email" placeholder={t('auth.email')} required value={signin.email} onChange={(e) => setSignin({ ...signin, email: e.target.value })} testId="signin-email-input" />
              <IconInput icon={Lock} type="password" placeholder={t('auth.password')} required value={signin.password} onChange={(e) => setSignin({ ...signin, password: e.target.value })} testId="signin-password-input" />
              <SubmitButton testId="signin-submit-button" loading={loading}>{t('nav.signin')}</SubmitButton>
            </form>
          </TabsContent>
          <TabsContent value="signup">
            <form onSubmit={onSignup} className="space-y-3" data-testid="signup-form">
              <IconInput icon={UserIcon} placeholder={t('auth.fullName')} required minLength={2} value={signup.name} onChange={(e) => setSignup({ ...signup, name: e.target.value })} testId="signup-name-input" />
              <IconInput icon={Mail} type="email" placeholder={t('auth.email')} required value={signup.email} onChange={(e) => setSignup({ ...signup, email: e.target.value })} testId="signup-email-input" />
              <div className="flex gap-2">
                <Select value={signup.country} onValueChange={(country) => setSignup({ ...signup, country })}>
                  <SelectTrigger className="w-28 h-11" data-testid="signup-country-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">{COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.flag} {c.code}</SelectItem>)}</SelectContent>
                </Select>
                <div className="flex-1"><IconInput icon={Phone} type="tel" placeholder={t('auth.phone')} value={signup.phone} onChange={(e) => setSignup({ ...signup, phone: e.target.value })} testId="signup-phone-input" /></div>
              </div>
              <IconInput icon={Lock} type="password" placeholder={t('auth.password')} required minLength={6} value={signup.password} onChange={(e) => setSignup({ ...signup, password: e.target.value })} testId="signup-password-input" />
              <SubmitButton testId="signup-submit-button" loading={loading}>{t('auth.create')}</SubmitButton>
            </form>
          </TabsContent>
        </Tabs>
        {error && <p className="mt-4 text-sm text-[#E8814A] text-center font-medium" data-testid="auth-error">{error}</p>}
        <p className="text-xs text-[#7A7468] text-center mt-6">
          {t('auth.terms')} <Link to="/" className="underline">{t('auth.termsLink')}</Link> {t('auth.and')} <Link to="/" className="underline">{t('auth.privacyLink')}</Link>.
        </p>
      </div>
    </div>
  );
};

export default SignIn;
